import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { HookTextarea } from '@/components/UI/HookForm/Form/TextArea';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { patchProjectTemplate } from '../../../../../api/Sherpa/ProjectTemplate';
import { constructionType, projectType } from '../../../../../constants/Project';
import { SherpaProjectReactQueryKey } from '../../../../../constants/ReactQueryKeys';
import { notifyUser } from '../../../../../store/Toast';
import { sanitizeResponseError } from '../../../../../utils';
import type { ProjectTemplateInterface } from '../../../types';
import { validationSchema } from '../../NewProjectTemplate/validationSchema';

interface EditProjectTemplateProps {
  handleModal: () => void;
  defaultValues?: ProjectTemplateInterface;
}

const EditProjectTemplateBase = ({ handleModal, defaultValues }: EditProjectTemplateProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const descriptionValues = useWatch({ name: 'description' });

  const updateProjectTemplate = useMutation(
    (payload: { data: Partial<ProjectTemplateInterface>; id: number }) =>
      patchProjectTemplate({
        data: payload.data,
        templateId: payload.id,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_PROJECT_TEMPLATE]);
        handleModal();
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Project Template updated successfully',
            toastType: 'success',
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );
  const handleSave = (data: any) => {
    const { id, name, description, constructionType, projectType } = data;
    const updatedData = {
      name,
      description,
      constructionType: constructionType.value,
      projectType: projectType.value,
    };
    updateProjectTemplate.mutate({ data: updatedData, id });
  };

  return (
    <HookForm onSubmit={handleSave} className='space-y-5'>
      <HookInput type='text' label='Template Name' name='name' />
      <HookReactSingleSelect label='Construction Type' name='constructionType' options={constructionType} />
      <HookReactSingleSelect label='Project Type' name='projectType' options={projectType} />
      <HookTextarea name='description' label='Description' maxLength={250} />
      <span className='text-xs'>{descriptionValues.length}/250 characters</span>
      <div className='flex gap-4 mt-5 justify-end'>
        <Button label='Cancel' type='button' btnStyle='btn-neutral' onClick={handleModal} />
        <Button label={`${updateProjectTemplate.isLoading ? 'Saving...' : 'Save'}`} type='submit' btnStyle='btn-primary' />
      </div>
    </HookForm>
  );
};

const EditProjectTemplate = (props: EditProjectTemplateProps) => {
  const transformedDefaultValues = {
    ...props.defaultValues,
    constructionType: {
      label: props.defaultValues?.constructionType,
      value: props.defaultValues?.constructionType,
    },
    projectType: {
      label: props.defaultValues?.projectType,
      value: props.defaultValues?.projectType,
    },
  };
  return (
    <Modal>
      <h2 className='mt-0'>Edit Template Information</h2>
      <HookFormProvider defaultValues={transformedDefaultValues} resolver={yupResolver(validationSchema) as any} mode='all'>
        <EditProjectTemplateBase {...props} />
      </HookFormProvider>
    </Modal>
  );
};

export default EditProjectTemplate;
